import React, { useState } from 'react';
import './ResultDisplay.css';

function ResultDisplay({ result, onReset, frontPreview, backPreview }) {
  const [showRawJson, setShowRawJson] = useState(false);

  const data = result?.Result?.Data || {};
  const transaction = result?.Result?.Transaction || {};
  const transactionId = transaction.ID;
  // Images are shown WITHOUT any fetchable URL: Front/Back are the visitor's own
  // uploads (client-side data URLs); Portrait/Signature are base64 inlined by the
  // server in the scan response. Nothing here is a shareable/public link.
  const inlineImages = result?.Demo_Images || {};

  const getImageUrl = (type) => {
    if (type === 'front') return frontPreview || null;
    if (type === 'back') return backPreview || null;
    return inlineImages[type] || null;
  };
  const hasAnyImage = !!(frontPreview || backPreview || inlineImages.portrait || inlineImages.signature);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const renderStatusBadge = (statusCode) => {
    const statusMap = {
      4: { text: 'Completed', class: 'success' },
      3: { text: 'Processing', class: 'warning' },
      2: { text: 'Pending', class: 'info' },
      1: { text: 'Failed', class: 'error' }
    };
    const status = statusMap[statusCode] || { text: 'Unknown', class: 'default' };
    return <span className={`status-badge ${status.class}`}>{status.text}</span>;
  };

  return (
    <div className="result-display">
      {/* Header */}
      <div className="result-header">
        <div className="result-title">
          <div className="title-row">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="16" cy="16" r="14" stroke="#fe8029" strokeWidth="2"/>
              <path d="M10 16L14 20L22 12" stroke="#fe8029" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h2>Scan Complete</h2>
          </div>
          <div className="meta-row">
             {renderStatusBadge(transaction.Status_Code)}
             <span className="meta-id">ID: {transaction.ID || '-'}</span>
          </div>
        </div>
        <button className="reset-button" onClick={onReset}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 10C4 6.68629 6.68629 4 10 4C12.2208 4 14.1599 5.21171 15.1973 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M16 10C16 13.3137 13.3137 16 10 16C7.77924 16 5.84013 14.7883 4.80269 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M15 4V7H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M5 16V13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          New Scan
        </button>
      </div>

      <div className="result-dashboard">
        {/* Extracted Images Section - 2x2 Grid */}
        {hasAnyImage && (
          <div className="images-section">
            <div className="images-grid-2x2">
              {/* Row 1 */}
              <div className="image-card">
                <span className="image-label">Front</span>
                <div className="face-image-container">
                  <img 
                    src={getImageUrl('front')} 
                    alt="Front ID" 
                    className="face-image document"
                    onError={(e) => { e.target.parentElement.parentElement.style.display = 'none'; }}
                  />
                </div>
              </div>
              <div className="image-card">
                <span className="image-label">Portrait</span>
                <div className="face-image-container portrait-container">
                  <img 
                    src={getImageUrl('portrait')} 
                    alt="Portrait" 
                    className="face-image portrait"
                    onError={(e) => { e.target.parentElement.parentElement.style.display = 'none'; }}
                  />
                </div>
              </div>

              {/* Row 2 */}
              <div className="image-card">
                <span className="image-label">Back</span>
                <div className="face-image-container">
                  <img 
                    src={getImageUrl('back')} 
                    alt="Back ID" 
                    className="face-image document"
                    onError={(e) => { e.target.parentElement.parentElement.style.display = 'none'; }}
                  />
                </div>
              </div>
              <div className="image-card">
                <span className="image-label">Signature</span>
                <div className="face-image-container signature-container">
                  <img 
                    src={getImageUrl('signature')} 
                    alt="Signature" 
                    className="face-image signature"
                    onError={(e) => { e.target.parentElement.parentElement.style.display = 'none'; }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Personal Information Card */}
        <div className="info-card">
          <div className="info-card-header">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="6" r="4" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M2 18C2 14.134 5.582 11 10 11C14.418 11 18 14.134 18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <h3>Personal Information</h3>
          </div>
          <div className="info-card-body">
            <div className="info-row">
              <InfoItem label="First Name" value={data.First_Name} />
              <InfoItem label="Middle Name" value={data.Middle_Name} />
              <InfoItem label="Last Name" value={data.Last_Name} />
            </div>
            <div className="info-row">
              <InfoItem label="Date of Birth" value={formatDate(data.Date_Of_Birth)} />
              <InfoItem label="Gender" value={data.Sex} />
              <InfoItem label="Document Type" value={data.Document_Type} highlight />
            </div>
          </div>
        </div>

        {/* Document Details Card */}
        <div className="info-card">
          <div className="info-card-header">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="2" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M7 6H13M7 10H13M7 14H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <h3>Document Details</h3>
          </div>
          <div className="info-card-body">
            <div className="info-row">
              <InfoItem label="Licence Number" value={data.Licence_Number} highlight />
              <InfoItem label="Card Number" value={data.Card_Number} />
              <InfoItem label="Expiry Date" value={formatDate(data.Licence_Expiry)} />
            </div>
            <div className="info-row">
              <InfoItem label="Licence Type" value={data.Licence_Type} />
              <InfoItem label="Issuing State" value={data.State_Name} />
              <InfoItem label="Conditions" value={data.Conditions} />
            </div>
            <div className="info-row single">
              <InfoItem label="Processing Time" value={transaction.Processing_Time ? `${(transaction.Processing_Time / 1000).toFixed(2)}s` : null} />
            </div>
          </div>
        </div>

        {/* Front Address Card */}
        <div className="info-card">
          <div className="info-card-header">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 11C11.6569 11 13 9.65685 13 8C13 6.34315 11.6569 5 10 5C8.34315 5 7 6.34315 7 8C7 9.65685 8.34315 11 10 11Z" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10 18C10 18 16 13.5 16 8.5C16 5.18629 13.3137 2.5 10 2.5C6.68629 2.5 4 5.18629 4 8.5C4 13.5 10 18 10 18Z" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
            <h3>Front Address</h3>
          </div>
          <div className="info-card-body">
            {data.Front_Address && (
              <div className="address-banner">
                {data.Front_Address}
              </div>
            )}
            <div className="info-row">
              <InfoItem label="Unit" value={data.Front_Address_Breakdown?.Unit_Number} small />
              <InfoItem label="Street No." value={data.Front_Address_Breakdown?.Street_Number} small />
              <InfoItem label="Street Name" value={data.Front_Address_Breakdown?.Street_Name} />
              <InfoItem label="Street Type" value={data.Front_Address_Breakdown?.Street_Type} small />
            </div>
            <div className="info-row">
              <InfoItem label="Suburb" value={data.Front_Address_Breakdown?.Suburb || data.Suburb} />
              <InfoItem label="State" value={data.Front_Address_Breakdown?.State} small />
              <InfoItem label="Postcode" value={data.Front_Address_Breakdown?.Postcode || data.Postcode} small />
              <InfoItem label="Country" value={data.Country_Name} />
            </div>
          </div>
        </div>

        {/* Back Address Card - Always show, will display empty fields if no data */}
        <div className="info-card">
          <div className="info-card-header">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 11C11.6569 11 13 9.65685 13 8C13 6.34315 11.6569 5 10 5C8.34315 5 7 6.34315 7 8C7 9.65685 8.34315 11 10 11Z" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10 18C10 18 16 13.5 16 8.5C16 5.18629 13.3137 2.5 10 2.5C6.68629 2.5 4 5.18629 4 8.5C4 13.5 10 18 10 18Z" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
            <h3>Back Address</h3>
          </div>
          <div className="info-card-body">
            {data.Back_Address ? (
              <div className="address-banner">
                {data.Back_Address}
              </div>
            ) : (
              <div className="no-data-message">No back address data available</div>
            )}
            <div className="info-row">
              <InfoItem label="Unit" value={data.Back_Address_Breakdown?.Unit_Number} small />
              <InfoItem label="Street No." value={data.Back_Address_Breakdown?.Street_Number} small />
              <InfoItem label="Street Name" value={data.Back_Address_Breakdown?.Street_Name} />
              <InfoItem label="Street Type" value={data.Back_Address_Breakdown?.Street_Type} small />
            </div>
            <div className="info-row">
              <InfoItem label="Suburb" value={data.Back_Address_Breakdown?.Suburb} />
              <InfoItem label="State" value={data.Back_Address_Breakdown?.State} small />
              <InfoItem label="Postcode" value={data.Back_Address_Breakdown?.Postcode} small />
            </div>
          </div>
        </div>

      </div>

      {/* Raw JSON Toggle */}
      <div className="raw-json-section">
        <button 
          className="toggle-json-button"
          onClick={() => setShowRawJson(!showRawJson)}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4L1 8L4 12M12 4L15 8L12 12M10 2L6 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {showRawJson ? 'Hide' : 'Show'} Raw Data
        </button>
        
        {showRawJson && (
          <pre className="json-content">
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function InfoItem({ label, value, highlight, small }) {
  return (
    <div className={`info-item ${small ? 'small' : ''} ${highlight ? 'highlight' : ''}`}>
      <span className="info-label">{label}</span>
      <span className="info-value">{value || '-'}</span>
    </div>
  );
}

export default ResultDisplay;
